import { useEffect, useState, useSyncExternalStore } from 'react';
import { Navigate } from 'react-router';
import { clearAuth, getToken, getUser, patchUser, subscribe } from './auth';
import {
  getCustomer,
  getCustomerToken,
  subscribeCustomer,
} from './customer-auth';
import { getMe } from './professional-api';
import { pendingBookingResumePath } from './booking-draft';

/**
 * Subscribes a component to the localStorage-backed auth store. Returns
 * the live `{ token, user }` pair — re-renders the component when either
 * one changes.
 */
export function useAuth() {
  const token = useSyncExternalStore(subscribe, getToken, () => null);
  const user = useSyncExternalStore(subscribe, getUser, () => null);
  return { token, user, isAuthenticated: !!token };
}

/**
 * Basic guard for routes that need a logged-in professional regardless
 * of approval state. Used by onboarding pages (verify-identity,
 * verification-complete, account-info, account-settings) — pages a
 * pending pro should still be able to reach.
 */
export function RequirePro({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/professional/signin" replace />;
  }
  return <>{children}</>;
}

/**
 * Guard for routes that need a logged-in pro whose email has been
 * verified, but who may still be PENDING approval. Used by the
 * verify-identity step — a pro shouldn't reach the document uploader
 * until they've confirmed their email.
 *
 * Refreshes `email_verified_at` from /professional/me on mount so a pro
 * who just clicked the verification link in another tab gets in on
 * refresh instead of being bounced off a stale local cache.
 */
export function RequireVerifiedPro({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    getMe()
      .then((res) => {
        if (cancelled) return;
        const info: any = res?.info ?? {};
        const u = info.user ?? info;
        patchUser({
          status: u?.status,
          email_verified_at:
            u?.email_verified_at ?? user?.email_verified_at ?? null,
        });
      })
      .catch(() => {
        // Network / 401 — axios interceptor handles auth clearing.
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return <Navigate to="/professional/signin" replace />;
  }
  if (!user?.email_verified_at) {
    return (
      <Navigate
        to="/professional/signup/success"
        state={{ email: user?.email }}
        replace
      />
    );
  }
  return <>{children}</>;
}

/**
 * Stricter guard for routes that require an APPROVED professional —
 * dashboard, messenger, services, bookings, schedule, earnings, etc.
 *
 * Redirects pending pros to /professional/verify-identity so they can
 * complete onboarding before getting access to the rest of the app.
 *
 * On mount we also call `getMe()` to refresh `status` and
 * `email_verified_at` from the server. That way a pro who's just been
 * approved by an admin gets unblocked the moment they refresh the page
 * instead of having to log out and back in.
 */
export function RequireApprovedPro({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    getMe()
      .then((res) => {
        if (cancelled) return;
        const info: any = res?.info ?? {};
        const u = info.user ?? info;
        // Patch the local cache with whatever server-side fields we got.
        // Anything missing falls through and keeps the current value.
        patchUser({
          status: u?.status,
          email_verified_at:
            u?.email_verified_at ?? user?.email_verified_at ?? null,
          onboarding_step:
            info?.onboarding_step ?? user?.onboarding_step ?? null,
        });
      })
      .catch(() => {
        // Network / 401 — let the axios interceptor handle clearing
        // auth. The guard below will redirect on next render.
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return <Navigate to="/professional/signin" replace />;
  }
  // Check the terminal/blocked statuses first — those always sign
  // the user out regardless of email-verified state.
  if (
    user?.status === 'SUSPENDED' ||
    user?.status === 'DELETED' ||
    user?.status === 'REJECTED'
  ) {
    // No access at all — clear local auth and bounce to sign-in.
    clearAuth();
    return <Navigate to="/professional/signin" replace />;
  }
  // APPROVED pros are past onboarding. Their email was verified at
  // some point in the past (we don't allow APPROVED without a
  // verified email) — so even if the locally-cached
  // `email_verified_at` is null due to a stale patch, we must not
  // bounce them back to the signup-success screen. Render the
  // protected page and trust the server-side authorization.
  if (user?.status === 'APPROVED') {
    return <>{children}</>;
  }
  // Below this point the pro is non-APPROVED. Route them by
  // onboarding stage: unverified email → "check your inbox"; verified
  // email but not yet approved → identity / docs page.
  if (!user?.email_verified_at) {
    return (
      <Navigate
        to="/professional/signup/success"
        state={{ email: user?.email }}
        replace
      />
    );
  }
  // PENDING with verified email — finish onboarding before reaching the
  // dashboard. Resume mid-wizard pros (onboarding_step 1..10) at the wizard;
  // send step-0 pros (haven't cleared identity) to the documents page. This
  // mirrors ProSignInForm so a returning pro isn't dumped back at the start.
  if ((user?.onboarding_step ?? 0) >= 1) {
    return <Navigate to="/professional/service-offer" replace />;
  }
  return <Navigate to="/professional/verify-identity" replace />;
}

/**
 * Stricter sibling of `RequireApprovedPro`. Lets verified pros through
 * regardless of approval status but only AFTER they've completed the
 * post-verify wizard (services → location → certifications → payment).
 * Pros with `onboarding_completed_at == null` get sent to /service-offer
 * to finish — they cannot reach the dashboard, schedule, services,
 * earnings, etc. until that wizard is done.
 *
 * We have to read `onboarding_completed_at` from the server because the
 * cached auth user only carries auth-level fields; the dashboard pages
 * already do a fresh getMe() on mount so the extra fetch here is the
 * same cost as `RequireApprovedPro` pays.
 */
export function RequireOnboardedPro({
  children,
}: {
  children: React.ReactNode;
}) {
  const { isAuthenticated, user } = useAuth();
  // null = still checking, otherwise where we need to send the pro.
  const [target, setTarget] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    getMe()
      .then((res) => {
        if (cancelled) return;
        const info: any = res?.info ?? {};
        const u = info.user ?? info;
        patchUser({
          status: u?.status,
          email_verified_at:
            u?.email_verified_at ?? user?.email_verified_at ?? null,
        });
        // The funnel is: email verify → /verify-identity → /service-offer
        // → dashboard. `onboarding_completed_at` is the single source of
        // truth for "this pro has finished the post-signup walk" — the
        // wizard's final step stamps it, which means they cleared
        // verify-identity to get there. We don't gate on
        // verification_status here because it's a *review* signal
        // (admin's queue), not a funnel signal — and historically the
        // field could lag behind real document state, leaving onboarded
        // pros stuck on /verify-identity. The /verify-identity route
        // itself handles "no docs yet → upload them".
        if (!info.onboarding_completed_at) {
          setTarget('/professional/service-offer');
          return;
        }
        setTarget(null); // null = allowed
      })
      .catch(() => {
        // Leave target undefined; isAuthenticated check below + axios
        // interceptor handle auth errors.
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return <Navigate to="/professional/signin" replace />;
  }
  if (
    user?.status === 'SUSPENDED' ||
    user?.status === 'DELETED' ||
    user?.status === 'REJECTED'
  ) {
    clearAuth();
    return <Navigate to="/professional/signin" replace />;
  }
  if (!user?.email_verified_at) {
    return (
      <Navigate
        to="/professional/signup/success"
        state={{ email: user?.email }}
        replace
      />
    );
  }
  // Wait for the getMe round-trip before rendering anything sensitive,
  // otherwise pros would briefly see dashboard content before being
  // bounced.
  if (target === undefined) {
    return <RouteLoadingScreen />;
  }
  if (target) return <Navigate to={target} replace />;
  return <>{children}</>;
}

/**
 * Branded loading screen used while route guards are mid-decision. The
 * bare "Loading…" text on a blank page felt broken on slow networks —
 * this gives the customer / pro the Cherry Picked logo, a spinner,
 * and a soft brand backdrop so they know the app is alive.
 */
function RouteLoadingScreen() {
  return (
    <div className="min-h-screen bg-[#fcfaf7] flex flex-col items-center justify-center px-4">
      <div className="flex flex-col items-center gap-4">
        <div className="w-[64px] h-[64px] rounded-full border-4 border-[#ede8e9] border-t-[#8e2a2f] animate-spin" />
        <p
          className="font-['Playfair_Display',Georgia,serif] font-extrabold text-[#8e2a2f] text-[22px]"
          style={{ letterSpacing: '-0.02em' }}
        >
          Cherry Picked
        </p>
        <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[13px]">
          Loading your dashboard…
        </p>
      </div>
    </div>
  );
}

/**
 * Inverse of `RequirePro` / `RequireCustomer`. Used on the public auth-
 * entry routes (`/signin`, `/signup`, `/professional/signin`, etc.) so
 * an already-authenticated user can't accidentally land on a sign-in
 * page — we bounce them to the destination that matches their state:
 *
 *   - logged-in unverified pro → /professional/signup/success
 *   - logged-in verified pro  → /professional/dashboard
 *     (RequireOnboardedPro at the dashboard then forwards to
 *      /service-offer if onboarding_completed_at is null, OR renders
 *      the dashboard if onboarding is done — even for PENDING pros
 *      awaiting admin approval. Doing the second-stage routing there
 *      keeps `onboarding_completed_at` out of the local AuthUser
 *      cache.)
 *   - logged-in customer (unverified) → /signup/success
 *   - logged-in customer (verified) → /user-landing
 *   - not logged in → render the children
 *
 * Previously this hard-coded `/professional/verify-identity` for any
 * non-APPROVED pro — that worked when /verify-identity was the
 * post-signup catch-all KYC page, but after Phase 6 (Stripe Connect
 * owns identity, /verify-identity is just the residual insurance
 * uploader) sending mid-onboarding pros there is misleading.
 */
export function RedirectIfAuthed({ children }: { children: React.ReactNode }) {
  // Both stores must be read unconditionally because hooks can't branch.
  const proToken = useSyncExternalStore(subscribe, getToken, () => null);
  const proUser = useSyncExternalStore(subscribe, getUser, () => null);
  const customerToken = useSyncExternalStore(
    subscribeCustomer,
    getCustomerToken,
    () => null,
  );
  const customerUser = useSyncExternalStore(
    subscribeCustomer,
    getCustomer,
    () => null,
  );

  if (proToken) {
    if (!proUser?.email_verified_at) {
      return (
        <Navigate
          to="/professional/signup/success"
          state={{ email: proUser?.email }}
          replace
        />
      );
    }
    return <Navigate to="/professional/dashboard" replace />;
  }
  if (customerToken) {
    // A customer who signed up / signed in MID-BOOKING must be returned to
    // their booking, not bounced to the verify-email screen or the generic
    // landing — otherwise they're "kicked out of the booking flow and pushed
    // back to the start". The booking draft survives in localStorage and
    // checkout doesn't require a verified email, so resume off the draft
    // first. (Email verification stays available/async from the account.)
    const resume = pendingBookingResumePath();
    if (resume) return <Navigate to={resume} replace />;
    if (!customerUser?.email_verified_at) {
      return (
        <Navigate
          to="/signup/success"
          state={{ email: customerUser?.email }}
          replace
        />
      );
    }
    return <Navigate to="/user-landing" replace />;
  }
  return <>{children}</>;
}
